import { computed, Signal } from '@angular/core';
import { PathKind, SchemaPath, SchemaPathRules, validate } from '@angular/forms/signals';

/** Options of the {@link forbiddenCharacters} rule. */
export interface ForbiddenCharactersConfig {
    /**
     * Builds the user-facing message from the offending characters. Without one the
     * error carries the characters themselves, which is rarely what you want to show.
     */
    message?: (forbiddenCharacters: string) => string;
}

/**
 * Signal-forms schema rule rejecting characters the backend cannot store — emojis
 * above all. The equivalent in the reactive-forms widgets is the
 * `forbiddenCharacters` branch of `TextInputComponent.checkValidity`.
 *
 * The pattern is passed in as a signal rather than read from the store here, so the
 * rule makes no assumption about running inside an injection context and stays
 * trivially testable. Callers hand it `store.selectSignal(selectEmojisRegex)`, which
 * already falls back to `ConfigConstants.defaultEmojiRegex` while the config slice
 * is still empty — an unloaded slice therefore degrades to the built-in pattern
 * instead of skipping the check.
 *
 * @param path the string path to validate
 * @param pattern the forbidden-character regex source, as a signal
 * @param config optional message factory
 */
export function forbiddenCharacters<TPathKind extends PathKind = PathKind.Root>(
    path: SchemaPath<string, SchemaPathRules.Supported, TPathKind>,
    pattern: Signal<string>,
    config?: ForbiddenCharactersConfig,
): void {
    // rebuilt only when the config slice changes, rather than on every keystroke
    const matcher = computed(() => {
        const source = pattern();
        return source ? new RegExp(source, 'g') : undefined;
    });

    validate(path, (context) => {
        const forbiddenMatcher = matcher();
        const value = context.value();
        if (!forbiddenMatcher || !value) {
            return undefined;
        }

        const matches = value.match(forbiddenMatcher);
        if (!matches) {
            return undefined;
        }

        // the set removes duplicates, so each offending character is reported once
        const forbidden = [...new Set(matches)].join('');
        return { kind: 'forbiddenCharacters', message: config?.message?.(forbidden) ?? forbidden };
    });
}
